import { Injectable } from '@angular/core';
import { Router, CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

import { AuthenticationService } from '../services/authentication.service';
import { PatientAuthenticationService } from '../services/patientAuthentication.service';
import {UserService} from '../../../services/user.service';
import {Globals} from '../../../globals';
import {LocalStorageService} from '../../../services/local-storage.service';

/** Returns true when the JWT access token stored in localStorage is past its exp claim. */
function isJwtExpired(token: string): boolean {
  try {
    const payloadSegment = token.split('.')[1];
    if (!payloadSegment) {
      return true;
    }

    const base64 = payloadSegment
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const paddedBase64 = base64 + '='.repeat((4 - (base64.length % 4)) % 4);

    const payload = JSON.parse(atob(paddedBase64));
    return !!payload.exp && (payload.exp * 1000) < Date.now();
  } catch {
    return true; // Unparseable token — treat as expired so auth failure handling runs.
  }
}

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {

  patientRoutes: string[] = [
    '/patientportal'
  ];

  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private patientAuthService: PatientAuthenticationService,
    private userService: UserService,
    private globals: Globals,
    private localStorageService: LocalStorageService
  ) {
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const currentUser = this.localStorageService.currentUserData;
    const isReferrerSession = localStorage.getItem('isReferrerRole') === 'true' || !!localStorage.getItem('storedReferrerUser');
    const isPatientSession = localStorage.getItem('isPatientRole') === 'true' || !!localStorage.getItem('storedPatient');
    // Keep referrer/patient sessions out of TDO routes guarded by AuthGuard.
    if (isReferrerSession) {
      this.router.navigate(['/referrerportal/home']);
      return false;
    }
    if (isPatientSession) {
      this.router.navigate(['/patientportal/home']);
      return false;
    }

    if (!localStorage.getItem('storedUser') || !currentUser || (currentUser && !currentUser.authdata)) {
      this.redirectOnAuthFailure(route);
      return false;
    }

    // Proactively check JWT expiry so a page with a stale token never renders
    // with empty data — the user sees the login page immediately instead.
    const token = localStorage.getItem('TDOauthToken');
    if (token && isJwtExpired(token)) {
      this.authenticationService.logout();
      this.redirectOnAuthFailure(route);
      return false;
    }

    return true;
  }

  private redirectOnAuthFailure(route: ActivatedRouteSnapshot): void {
    const url = this.router.url;
    const isPopupRoute = url.includes('configuration') || url.includes('scheduler') ||
      url.includes('forms/builder') || url.includes('studio') || url.includes('reports') ||
      route.routeConfig?.path === 'configuration';

    if (isPopupRoute)
      window.close();
    else if (!url.includes('/login'))
      this.router.navigate(['/login']);
  }  

  // Check to see if the user using a TDO12 token.
  // If the user is logged in, log them out and ask for authentication
  hasTDO12Token(route: ActivatedRouteSnapshot) {
    for (const element of route.url) {
      const url = element;
      if (url.path === 'handleToken') {
        if (this.hasHandleTokenFlag()) {
          this.clearHandleTokenFlag();
          return false;
        } else {
          this.authenticationService.logout();
          this.setHandleTokenFlag();
          return true;
        }
      }
    }
    return false;
  }

  clearHandleTokenFlag() {
    localStorage.removeItem('handleTDO12TokenFlag');
  }

  setHandleTokenFlag() {
    localStorage.setItem('handleTDO12TokenFlag', 'true');
  }

  hasHandleTokenFlag() {
    const flag = localStorage.getItem('handleTDO12TokenFlag');
    return !!flag;
  }

}


